// store/slices/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { message } from 'antd';
import { API } from '@/api/config';
import api from '@/api';
import { ROLES, ROLE_PERMISSIONS } from '@/constants/roles';

// 初始状态
const initialState = {
  user: JSON.parse(localStorage.getItem('userInfo')),
  isAuthenticated: !!JSON.parse(localStorage.getItem('userInfo')),
  loading: false,
  error: null,
  permissions: [],
  role: ROLES.GUEST,
  token: localStorage.getItem('access_token') || null,
  access_token: localStorage.getItem('access_token') || null,
  refreshToken: localStorage.getItem('refreshToken') || null,
};

// 异步Thunks
export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${API}/api/admin/login`, { username, password });
      
      const { token, refreshToken, user } = response.data.data;
      
      
      // 存储token
      localStorage.setItem('access_token', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('userInfo', JSON.stringify(user));
      
      return { token, refreshToken, user };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '登录失败');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      // const token = localStorage.getItem('access_token');
      // if (token) {
      //   await api.post('/auth/logout', {}, {
      //     headers: { Authorization: `Bearer ${token}` }
      //   });
      // }
      
      // 清除本地存储
      localStorage.removeItem('access_token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userInfo');
      
      return null;
    } catch (error) {
      // 即使API调用失败，也清除本地存储
      localStorage.removeItem('access_token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userInfo');
      return rejectWithValue(error.response?.data?.message || '登出失败');
    }
  }
);

export const logout_user = createAsyncThunk(
  'user/logout_user',
  async (_, { rejectWithValue }) => {
    try {
      
      const access_token = localStorage.getItem('access_token');
      // if (access_token) {
      //   await api.post('/auth/logout', {}, {
      //     headers: { Authorization: `Bearer ${access_token}` }
      //   });
      // }
      
      // 清除本地存储
      localStorage.removeItem('access_token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userInfo');
      
      return null;
    } catch (error) {
      // 即使API调用失败，也清除本地存储
      localStorage.removeItem('access_token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userInfo');
      return rejectWithValue(error.response?.data?.message || '登出失败');
    }
  }
);

export const login_by_scan = createAsyncThunk(
  'auth/login_by_scan',
  async ({ token, userInfo }, { rejectWithValue }) => {
    try {

      // 存储token
      localStorage.setItem('access_token', token);
      // localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('userInfo', JSON.stringify(userInfo));
      
      return { token, userInfo, user: userInfo, isAuthenticated: true };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '登录失败');
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '注册失败');
    }
  }
);

export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = state.auth.token || localStorage.getItem('access_token');
      
      if (!token) {
        throw new Error('No token found');
      }
      
      // 验证token有效性
      const response = await api.get('/auth/verify', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      return response.data.user;
    } catch (error) {
      // 如果token无效，清除本地存储
      localStorage.removeItem('access_token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userInfo');
      return rejectWithValue('认证已过期，请重新登录');
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const refreshToken = state.auth.refreshToken || localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        throw new Error('No refresh token');
      }
      
      const response = await api.post('/auth/refresh', { refreshToken });
      const { token, newRefreshToken } = response.data;
      
      // 更新存储
      localStorage.setItem('access_token', token);
      localStorage.setItem('refreshToken', newRefreshToken);
      
      return { token, refreshToken: newRefreshToken };
    } catch (error) {
      // 刷新token失败，清除存储
      localStorage.removeItem('access_token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userInfo');
      return rejectWithValue('Token刷新失败');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (userData, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      
      const response = await api.put('/auth/profile', userData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // 更新本地存储
      const updatedUser = { ...state.auth.user, ...response.data.user };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      return response.data.user;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '更新失败');
    }
  }
);

export const updateUserRole = createAsyncThunk(
  'auth/updateUserRole',
  async ({ userId, role }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      
      const response = await api.put(`/admin/users/${userId}/role`, { role }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '角色更新失败');
    }
  }
);

// 工具函数：获取用户权限
const getUserPermissions = (role) => {
  return ROLE_PERMISSIONS[role] || [];
};

// 工具函数：检查权限
export const hasPermission = (permissions, requiredPermission) => {
  if (!permissions || permissions.length === 0) return false;
  
  // 如果用户有所有权限
  if (permissions.includes('*')) return true;
  
  // 检查具体权限
  return permissions.includes(requiredPermission);
};

// 工具函数：检查角色
export const hasRole = (userRole, requiredRoles) => {
  if (!requiredRoles || requiredRoles.length === 0) return true;
  if (!userRole) return false;
  
  return requiredRoles.includes(userRole);
};

// 创建slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // 同步actions
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      if (action.payload) {
        state.role = action.payload.role || ROLES.GUEST;
        state.permissions = getUserPermissions(state.role);
      }
    },
    
    setToken: (state, action) => {
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      localStorage.setItem('access_token', action.payload.token);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
    },
    
    clearAuth: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.token = null;
      state.refreshToken = null;
      state.permissions = [];
      state.role = ROLES.GUEST;
      localStorage.removeItem('access_token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userInfo');
    },
    
    updateUserInfo: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },
    
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    
    setError: (state, action) => {
      state.error = action.payload;
    },
    showLoginModal: (state) => {
      state.loginModalVisible = true;
    },
    hideLoginModal: (state) => {
      state.loginModalVisible = false;
    },
  },
  
  extraReducers: (builder) => {
    builder
      // login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.role = action.payload.user.role || ROLES.USER;
        state.permissions = getUserPermissions(state.role);
        message.success('登录成功');
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        message.error(action.payload);
      })
      
      // logout
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.permissions = [];
        state.role = ROLES.GUEST;
        message.success('已退出登录');
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // 即使失败也清除状态
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.permissions = [];
        state.role = ROLES.GUEST;
        message.error(action.payload);
      })
      
      // register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state) => {
        state.loading = false;
        message.success('注册成功，请登录');
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        message.error(action.payload);
      })
      
      // checkAuth
      // .addCase(checkAuth.pending, (state) => {
      //   state.loading = true;
      //   state.error = null;
      // })
      // .addCase(checkAuth.fulfilled, (state, action) => {
      //   state.loading = false;
      //   state.isAuthenticated = true;
      //   state.user = action.payload;
      //   state.role = action.payload.role || ROLES.USER;
      //   state.permissions = getUserPermissions(state.role);
      // })
      // .addCase(checkAuth.rejected, (state, action) => {
      //   state.loading = false;
      //   state.error = action.payload;
      //   state.isAuthenticated = false;
      //   state.user = null;
      //   state.role = ROLES.GUEST;
      //   state.permissions = [];
      // })
      
      // refreshToken
      .addCase(refreshToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.user = null;
        state.role = ROLES.GUEST;
        state.permissions = [];
        message.error('登录已过期，请重新登录');
      })
      
      // updateProfile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        message.success('个人信息更新成功');
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        message.error(action.payload);
      })
      
      // updateUserRole
      .addCase(updateUserRole.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserRole.fulfilled, (state, action) => {
        state.loading = false;
        // 如果是更新当前用户的角色
        if (state.user && state.user.id === action.payload.userId) {
          state.user.role = action.payload.role;
          state.role = action.payload.role;
          state.permissions = getUserPermissions(action.payload.role);
          localStorage.setItem('user', JSON.stringify(state.user));
        }
        message.success('角色更新成功');
      })
      .addCase(updateUserRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        message.error(action.payload);
      });
  },
});

// 导出actions
export const { 
  setUser, 
  setToken, 
  clearAuth, 
  updateUserInfo, 
  setLoading, 
  setError ,
  showLoginModal, hideLoginModal
} = authSlice.actions;

// 导出选择器
export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUserRole = (state) => state.auth.role;
export const selectUserPermissions = (state) => state.auth.permissions;
export const selectLoading = (state) => state.auth.loading;
export const selectToken = (state) => state.auth.token;

// 检查权限的选择器
export const selectHasPermission = (requiredPermission) => (state) => {
  return hasPermission(state.auth.permissions, requiredPermission);
};

export const selectHasRole = (requiredRoles) => (state) => {
  return hasRole(state.auth.role, requiredRoles);
};

// 导出reducer
export default authSlice.reducer;